import React, { FC } from 'react';
import styled from 'styled-components';
import Pagination from './Pagination';
import Navigation from './Navigation';
import PlayPause from './PlayPause';
import useIsMobile from '../../hooks/useIsMobile';

interface ControlsProps {
  theme?: string;
  mobileTheme?: string;
  position: string;
  sysId: string;
  navigationArrows: boolean;
  autoplay?: number;
  isPlaying: boolean;
  current: number;
  total: number;
  progress: number;
  onPlayPause: () => void;
  contentAlignment?: string;
  mobileContentAlignment?: string;
  ghostNavEnabled?: boolean;
  showControls?: boolean;
  onPrevClick?: () => void;
  onNextClick?: () => void;
}

const Wrapper = styled.div<{ $position: string; $ghostNavEnabled?: boolean; }>`
  display: flex;
  gap: 16px;
  z-index: 3;
  box-sizing: border-box;

  ${({ $position }) => $position == 'right' && `
    margin-left: auto; // push to far right in row
  `}

  ${({ $position }) =>
    $position == 'left' || $position == 'right'
      ? `
        flex-direction: column;
        align-items: center;
        justify-content: space-between;
        height: 100%;
        padding-top: 245px;
        padding-bottom: 145px;
      `
      : `
        width: 100%;
        flex-direction: row;
        align-items: center;
        left: 50%;
        transform: translateX(-50%);
      `}
`;

const MobileWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  width: 90%;
  margin: 0 auto;
  position: absolute;
  bottom: 24px;
  left: 0;
  right: 0;
  z-index: 10;
`;

const CenterPagination = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`;

const CarouselControls: FC<ControlsProps> = ({
  theme,
  mobileTheme,
  position,
  sysId,
  navigationArrows,
  autoplay,
  isPlaying,
  current,
  total,
  progress,
  onPlayPause,
  ghostNavEnabled,
  onPrevClick,
  onNextClick,
}) => {
  const isMobile = useIsMobile();
  const activeTheme = isMobile ? mobileTheme : theme;

  if (isMobile) {
    return (
      <MobileWrapper className="carousel-controls">
        <Navigation
          theme={activeTheme}
          position="bottom"
          sysId={sysId}
          navigationArrows={true}
          direction="prev"
          onPrevClick={onPrevClick}
        />
        <CenterPagination>
          <Pagination
            theme={activeTheme}
            position="bottom"
            total={total}
            current={current}
            progress={progress}
          />
        </CenterPagination>
        <Navigation
          theme={activeTheme}
          position="bottom"
          sysId={sysId}
          navigationArrows={true}
          direction="next"
          onNextClick={onNextClick}
        />
      </MobileWrapper>
    );
  }

  return (
    <Wrapper $position={position} $ghostNavEnabled={ghostNavEnabled} className="carousel-controls">
      {autoplay && autoplay > 0 && (
        <PlayPause isPlaying={isPlaying} onClick={onPlayPause} theme={activeTheme} />
      )}

      <Pagination
        theme={activeTheme}
        position={position}
        total={total}
        current={current}
        progress={progress}
      />

      {navigationArrows && (
        <Navigation
          theme={activeTheme}
          position={position}
          sysId={sysId}
          navigationArrows={navigationArrows}
          onPrevClick={onPrevClick}
          onNextClick={onNextClick}
        />
      )}
    </Wrapper>
  );
};

export default CarouselControls;